/**
 * API Endpoints Configuration
 * 
 * Centralized endpoint paths for better organization and maintenance.
 * Use with base URLs from env.js
 * 
 * Usage:
 * import ENDPOINTS from '../config/endpoints.js';
 * import ENV from '../config/env.js';
 * const url = `${ENV.AUTH_BASE_URL}${ENDPOINTS.AUTH.LOGIN}`;
 */

const ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: "/login",
    REGISTER: "/register",
    FORGOT_PASSWORD: "/forgot-password-code",
    VERIFY_RESET_CODE: "/verify-reset-code",
    RESET_PASSWORD: "/reset-password",
    REFRESH_TOKEN: "/refresh",
    PUBLIC_FORWARD: "/public-forward",
    SET_NEW_PASSWORD: "/set-new-password",
  },

  // User management endpoints
  USER: {
    PROFILE: "/api/users/profile",
    PREFERENCES: "/api/users/preferences",
    THEME: "/api/users/preferences/theme",
  },

  // Brand endpoints
  BRANDS: {
    ALL: "/api/v1/brands/all",
    SEARCH: "/api/v1/brands/search", // + ?q={query} - Search brands (Spring Page response)
    DASHBOARD_SUMMARY: "/api/v1/brands/dashboard/summary",
    DASHBOARD_BRANDS: "/api/v1/brands/dashboard/brands",
    DETAILS: "/api/v1/brands/dashboard/brands", // + /{brandId}/details
    BRAND_INFO: "/api/v1/brands", // + /{brandId} - New endpoint for brand info
    REVIEWS: "/api/v1/reviews/brands", // + /{brandId} - Brand reviews endpoint
    DOWNLOAD_KIT: "/api/v1/brands/download-kit", // + /{brandId}/download-kit
    CLAIM: "/api/rbac/brand/claim", // Brand claim endpoint
    OFFERINGS: "/brand/offerings", // AI-suggested sell / don't-sell lists for an analyzed brand
    COMPETITORS: "/brand/competitors", // AI-discovered competitors + authority scores for an analyzed brand
    CONTENT_PLAN: "/brand/content-plan", // AI-generated content plan (topics + target keywords) for an analyzed brand
    AGENT_PROMPT: "/brand/agent-prompt", // AI-generated SEO agent standing prompt, grounded in sell items + competitors + saved business goals
    EXECUTION_PLAN: "/brand/execution-plan", // AI-generated six-phase SEO execution plan (audit/technical/optimize/content/GSC/monitoring) for an analyzed brand
  },

  // Support endpoints
  SUPPORT: {
    FAQS: "/api/faqs",
  },

  // Email Sharing endpoints
  EMAIL_SHARING: {
    SHARE: "/api/email-sharing/share",
  },

  // Documentation endpoints
  DOCUMENTATION: {
    BASE: "/api/documentation",
    GUIDE_STEPS: "/api/documentation/guide-steps",
    SEARCH: "/api/documentation/search",
    TYPE_GUIDE: "/api/documentation/type/GUIDE",
    TYPE_EDITOR: "/api/documentation/type/EDITOR",
    PUBLISHED: "/api/documentation/published/all",
  },

  // Chat endpoints
  CHAT: {
    POST: "/api/chat",
  },

  // Review endpoints
  REVIEWS: {
    QUESTIONS: "/api/v1/reviews/questions",
    SUBMIT: "/api/v1/reviews",
    USER: "/api/v1/reviews/mine",
    SINGLE: "/api/v1/reviews", // + /{reviewId}
    UPDATE: "/api/v1/reviews", // + /{reviewId}
    BRAND: "/api/v1/reviews/brands", // + /{brandId}
    CHECK_REVIEW: "/api/v1/reviews/brands", // + /{brandId}/mine
    ALL_QUESTIONS: "/api/v1/reviews/questions/all",
    CREATE_QUESTIONS: "/api/v1/reviews/questions",
    UPDATE_QUESTIONS: "/api/v1/reviews/questions", // + /{questionId}
    BRANDS_WITH_REVIEWS: "/api/v1/reviews/admin/brands",
    ALL: "/api/v1/reviews/admin/reviews",
    STATS_TOTAL: "/api/v1/reviews/admin/stats",
    BRAND_STATS: "/api/v1/reviews/brands", // + /{brandId}/stats
    BULK_STATUS_UPDATE: "/api/v1/reviews/admin/statuses",
    BRAND_REVIEW_SUMMARY: "/api/v1/reviews/admin/brand-summary",
    BRAND_REVIEW_SUMMARY_POST: "/api/v1/reviews/admin/brand-summary-post",
    BRAND_PENDING: "/api/v1/reviews/admin/brands", // + /{brandId}/pending
    ADMIN_BRAND_ALL: "/api/v1/reviews/admin/brands", // + /{brandId}/reviews
    RECENT: "/api/v1/reviews/admin/recent",
    REPLIES: "/api/v1/reviews", // + /{reviewId}/replies
  },
  // Reaction endpoints
  REACTIONS: {
    BASE: "/api/reactions",
    LIKE: "/api/reactions/{targetType}/{targetId}/like",
    DISLIKE: "/api/reactions/{targetType}/{targetId}/dislike",
  },

  // Alerts endpoints
  ALERTS: {
    STATS: "/api/alerts/stats",
    LIST: "/api/alerts",
    MARK_READ: "/api/alerts", // + /{alertId}/read
    TOGGLE_STAR: "/api/alerts", // + /{alertId}/star
    DELETE: "/api/alerts", // + /{alertId}
    BULK_MARK_READ: "/api/alerts/bulk",
    CLEANUP: "/api/alerts/cleanup",
  },

  // Warmup endpoints
  WARMUP: {
    MY_ACCOUNTS: "/api/warmup/my-accounts",
    CREATE_ACCOUNT: "/api/warmup/create-account",
    ACCOUNT_DETAIL: "/api/warmup/accounts", // + /{accountId}
    ACCOUNT_STATUS: "/api/warmup/accounts", // + /{accountId}/status
    ACCOUNT_CONFIGURATION: "/api/warmup/accounts", // + /{accountId}/configure
    ACCOUNT_CONFIGURATION_GET: "/api/warmup/accounts", // + /{accountId}/configuration
    ACCOUNT_PAUSE: "/api/warmup/accounts", // + /{accountId}/pause
    ACCOUNT_RESUME: "/api/warmup/accounts", // + /{accountId}/resume
    AUTO_VALIDATE_DKIM: "/api/warmup/accounts", // + /{accountId}/auto-validate-dkim
    CUSTOM_SMTP_CONFIG: "/api/warmup/accounts", // + /{accountId}/custom-smtp-config (GET / PUT)
    REVERIFY: "/api/warmup/accounts", // + /{accountId}/reverify (POST)
    REAUTH_CUSTOM: "/api/warmup/accounts", // + /{accountId}/reauth-custom (POST)
    CUSTOM_CONTACTS_UPLOAD: "/api/warmup/custom-contacts/upload", // multipart upload endpoint
    CUSTOM_CONTACTS_CONTACTS: "/api/warmup/custom-contacts/contacts", // + ?accountId={id}
    CUSTOM_CONTACTS_DAILY_STATUS: "/api/warmup/custom-contacts/status/daily", // + ?accountId={id}
    CUSTOM_CONTACTS_UPLOADED_EMAILS: "/api/warmup/custom-contacts/uploaded-emails", // + ?accountId={id}
    RECEIVERS: "/api/warmup/receivers",
    ALL_TEMPLATES: "/api/warmup/all-templates",
    GENERATE_TEMPLATE: "/api/warmup/templates/generate",
    CREATE_TEMPLATE: "/api/warmup/create-templates",
    UPDATE_TEMPLATE: "/api/warmup/templates", // + /{id}
    TEMPLATE_CATEGORIES: "/api/warmup/template-categories",
    PROVIDER_CONFIG: "/api/warmup/ai-provider/config",
    OAUTH: {
      GOOGLE_AUTHORIZE: "/api/warmup/oauth/google/authorize",
      GOOGLE_EXCHANGE: "/api/warmup/oauth/google/exchange",
      GOOGLE_REFRESH: "/api/warmup/oauth/google/refresh", // + /{accountId}
      GOOGLE_REVALIDATE: "/api/warmup/oauth/google/revalidate", // + /{accountId}
      MICROSOFT_AUTHORIZE: "/api/warmup/oauth/microsoft/authorize",
      MICROSOFT_EXCHANGE: "/api/warmup/oauth/microsoft/exchange",
      MICROSOFT_REFRESH: "/api/warmup/oauth/microsoft/refresh", // + /{accountId}
      MICROSOFT_REVALIDATE: "/api/warmup/oauth/microsoft/revalidate", // + /{accountId}
      REAUTHENTICATE: "/api/warmup/oauth/reauthenticate", // + /{accountId}
    },
  },

  // LinkedIn agent endpoints
  LINKEDIN: {
    AGENT_CONFIGURATION: "/api/linkedin/agent-configuration", // GET / PUT + ?tenantId={tenantId}
    AGENT_CONFIGURATION_DRAFT: "/api/linkedin/agent-configuration/draft", // PATCH + ?tenantId={tenantId} — debounced autosave
    AGENT_CONFIGURATION_EDIT: "/api/linkedin/agent-configuration/edit", // PATCH + ?userId={userId} — edits an existing (already-launched) configuration from Agent Settings
    AGENT_CONFIGURATION_HISTORY_LATEST: "/api/linkedin/agent-configuration/history/latest", // GET + ?userId={userId} — most recent history snapshot (what the config looked like before the last save), for the "Generate Prompt" old-vs-new diff
    AGENT_CONFIGURATION_MODE: "/api/linkedin/agent-configuration/mode", // PATCH { mode } + ?tenantId={tenantId} — set/clear MANUAL|AUTOMATE pick
    AGENT_CONFIGURATION_SUGGESTIONS_GENERATED: "/api/linkedin/agent-configuration/suggestions-generated", // PATCH (no body) + ?tenantId={tenantId} — marks Automate suggestion generation as already run
    AGENT_CONFIGURATION_SCRAPED_COMPANIES: "/api/linkedin/agent-configuration/companies", // GET — companies scraped via "View Company", for the AI-suggest picker
    AGENT_CONFIGURATION_SUGGEST: "/api/linkedin/agent-configuration/suggest", // POST { companyUrl } — AI-generated wizard field suggestions
    ORGANIZATION: "/api/linkedin/organization", // GET / POST
    ORGANIZATION_REGENERATE: "/api/linkedin/organization/regenerate", // POST — rebuilds the team + generates a fresh strategy_prompt even if one already exists
  },
};

export default ENDPOINTS;
